import type {
    FailedTaxTinderSuggestion,
    TaxTinderFailureReason,
    TaxTinderSuggestionApplication,
} from "../../../../../core/api/use-apply-tax-tinder-suggestions/useApplyTaxTinderSuggestions";
import type { TaxTinderSuggestionGroupDto } from "../../../../../core/api/use-get-tax-tinder-suggestions/TaxTinderSuggestionGroupDto";

export type TaxTinderFailedItem = {
    fulfilmentName: string;
    taxPayerName: string;
    reason: TaxTinderFailureReason;
};

export type TaxTinderApplySummary = {
    fulfilments: number;
    taxPayers: number;
    failedFulfilments: number;
    failedItems: TaxTinderFailedItem[];
};

export const TAX_TINDER_FAILURE_REASON_KEYS: Record<
    TaxTinderFailureReason,
    string
> = {
    ALREADY_ASSOCIATED:
        "views.home.components.tax_tinder.success.failed_reason.already_associated",
    UNKNOWN: "views.home.components.tax_tinder.success.failed_reason.unknown",
};

export const makeTaxTinderRowKey = (
    workspaceId: string,
    levyId: string,
    year: number,
) => `${workspaceId}::${levyId}::${year}`;

const rowKeyOf = (suggestion: TaxTinderSuggestionApplication) =>
    makeTaxTinderRowKey(
        suggestion.taxPayerWorkspaceId,
        suggestion.levyId,
        suggestion.year,
    );

export const buildApplyTaxTinderSuggestionsPayload = (
    data: TaxTinderSuggestionGroupDto[],
    isRowChecked: (rowKey: string) => boolean,
): TaxTinderSuggestionApplication[] => {
    const suggestions: TaxTinderSuggestionApplication[] = [];
    data.forEach((group) => {
        group.suggestedFulfilments.forEach((fulfilment) => {
            const rowKey = makeTaxTinderRowKey(
                group.workspaceId,
                fulfilment.levyId,
                fulfilment.accrualYear,
            );
            if (!isRowChecked(rowKey)) return;
            suggestions.push({
                levyId: fulfilment.levyId,
                year: fulfilment.accrualYear,
                taxPayerWorkspaceId: group.workspaceId,
            });
        });
    });
    return suggestions;
};

type TaxTinderRowNames = Omit<TaxTinderFailedItem, "reason">;

// Anything the BFF sends that is not a reason we know about is shown as
// "UNKNOWN": the dialog has no copy for a reason it has never heard of.
// Object.hasOwn and not `in`, so inherited names such as "constructor" or
// "toString" do not pass as reasons and hand t() a function.
const normalizeReason = (reason?: string): TaxTinderFailureReason =>
    reason !== undefined &&
    Object.hasOwn(TAX_TINDER_FAILURE_REASON_KEYS, reason)
        ? (reason as TaxTinderFailureReason)
        : "UNKNOWN";

const indexRowNames = (data: TaxTinderSuggestionGroupDto[]) => {
    const namesByRowKey = new Map<string, TaxTinderRowNames>();
    data.forEach((group) => {
        group.suggestedFulfilments.forEach((fulfilment) => {
            const rowKey = makeTaxTinderRowKey(
                group.workspaceId,
                fulfilment.levyId,
                fulfilment.accrualYear,
            );
            namesByRowKey.set(rowKey, {
                fulfilmentName: fulfilment.fulfilmentName,
                taxPayerName: group.description,
            });
        });
    });
    return namesByRowKey;
};

export const summarizeTaxTinderApply = (
    data: TaxTinderSuggestionGroupDto[],
    suggestions: TaxTinderSuggestionApplication[],
    failed: FailedTaxTinderSuggestion[],
): TaxTinderApplySummary => {
    const failedRowKeys = new Set(failed.map(rowKeyOf));
    const appliedTaxPayerIds = new Set<string>();
    let appliedFulfilments = 0;
    suggestions.forEach((suggestion) => {
        if (failedRowKeys.has(rowKeyOf(suggestion))) return;
        appliedFulfilments += 1;
        appliedTaxPayerIds.add(suggestion.taxPayerWorkspaceId);
    });

    const namesByRowKey = indexRowNames(data);
    const failedItems: TaxTinderFailedItem[] = [];
    // A failed tuple the suggestions no longer describe still counts in
    // failedFulfilments, it just has no name to show.
    failed.forEach((item) => {
        const names = namesByRowKey.get(rowKeyOf(item));
        if (!names) return;
        failedItems.push({ ...names, reason: normalizeReason(item.reason) });
    });

    return {
        fulfilments: appliedFulfilments,
        taxPayers: appliedTaxPayerIds.size,
        failedFulfilments: failed.length,
        failedItems,
    };
};
